import { useSelector } from 'react-redux';
import { logError, logger } from '@utils/usefulFuncs';
import { createAuthedEventSource } from '@utils/streamAuth';
import { gridTableResultsActions } from '@constants/actionTypes';
import { BASEURL } from '@services/network';

const createStream = async (resultId, dispatch, prevTableRowsLength, columnLength) => {
  let source = null;

  const promise = new Promise((resolve, reject) => {
    if (!resultId) {
      return reject('resultId was missing');
    }

    source = createAuthedEventSource(
      `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_context=true`
    );

    const onMessage = function (event) {
      try {
        const streamData = JSON.parse(event?.data || '{}');
        const id = streamData?.question_index;
        logger('useTableResults onMessage', 'received', {
          runId: resultId,
          rowIndex: id,
          finalAnswer: streamData?.final_answer === true,
          answerLength: streamData?.answer?.length || 0,
        });
        if (id == null) return;
        dispatch({
          type: gridTableResultsActions.updateResult,
          payload: {
            messageData: { id, message: streamData.answer || '' },
            columnLength,
            resultId,
            prevTableRowsLength,
          },
        });
      } catch (e) {
        logError('error caught in stream', e);
      }
    };
    const onOpen = function () {};
    const onError = function (e) {
      try {
        dispatch({
          type: gridTableResultsActions.error,
          payload: e,
        });
        logError('error received in stream', e);
        reject('error received in stream');
      } catch (e) {
        logError('error caught in stream', e);
        reject('error caught in stream');
      }
    };
    const onComplete = function (event) {
      try {
        const { data } = event;
        const streamData = JSON.parse(data);
        const answers = streamData?.response?.answers || [];
        const answersWithContexts = [];
        answers.forEach((row) => {
          if (!row || row.question_index == null) return;
          const existing = answersWithContexts[row.question_index];
          if (!existing || row.final_answer) {
            answersWithContexts[row.question_index] = {
              answer: row.answer || '',
              context: row.context,
              context_with_all_headers: row.context_with_all_headers,
              sources: row.sources,
            };
          }
        });
        dispatch({
          type: gridTableResultsActions.complete,
          payload: { results: answersWithContexts, columnLength, resultId, prevTableRowsLength },
        });
        source.removeEventListener('open', onOpen);
        source.removeEventListener('error', onError);
        source.removeEventListener('message', onMessage);
        source.close();
        resolve('complete');
      } catch (e) {
        logError('error caught in stream', e);
        reject('error caught in stream');
      }
    };

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener('message', onMessage);
    source.addEventListener('complete', onComplete);
  });

  return {
    promise,
    cancel: () => {
      if (source) {
        source.close();
      }
    },
  };
};

export const streamTableResults = (resultId, dispatch, prevTableRowsLength, columnLength) => {
  return createStream(resultId, dispatch, prevTableRowsLength, columnLength);
};

export const useTableResults = (cellId) => {
  const resultsLookup = useSelector((state) => state.gridTableResults.resultLookup);
  return resultsLookup[cellId] || null;
};
